import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, PlusCircle, ArrowUpRight, Wallet as WalletIcon, Download,
  ListOrdered, History, Bell, LifeBuoy, User, LogOut, Menu, X } from "lucide-react";
import { Logo, TelegramButton } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/add-usdt", label: "Add USDT", icon: PlusCircle },
  { to: "/app/sell", label: "Sell USDT", icon: ArrowUpRight },
  { to: "/app/wallet", label: "Wallet", icon: WalletIcon },
  { to: "/app/deposits", label: "Deposits", icon: Download },
  { to: "/app/orders", label: "Orders", icon: ListOrdered },
  { to: "/app/transactions", label: "History", icon: History },
  { to: "/app/notifications", label: "Notifications", icon: Bell },
  { to: "/app/support", label: "Support", icon: LifeBuoy },
  { to: "/app/profile", label: "Profile", icon: User },
];

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [tg, setTg] = useState("");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/settings/public").then((r) => setTg(r.data.telegram_url)).catch(() => {});
    const load = () => api.get("/customer/notifications").then((r) =>
      setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const doLogout = async () => { await logout(); nav("/login"); };

  const sidebar = (
    <nav className="flex flex-col gap-1 p-3">
      {NAV.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} onClick={() => setOpen(false)} data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition
            ${isActive ? "bg-blue-600 text-white shadow-sm shadow-blue-600/30" : "text-slate-600 hover:bg-slate-100"}`}>
          <Icon className="w-[18px] h-[18px]" />
          {label}
          {label === "Notifications" && unread > 0 && (
            <span className="ml-auto bg-rose-500 text-white text-xs rounded-full px-2 py-0.5" data-testid="nav-unread-badge">{unread}</span>
          )}
        </NavLink>
      ))}
      <button onClick={doLogout} data-testid="logout-button"
        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 transition mt-2">
        <LogOut className="w-[18px] h-[18px]" /> Logout
      </button>
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-16 max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Logo />
          </div>
          <div className="flex items-center gap-3">
            <TelegramButton url={tg} className="hidden sm:inline-flex" />
            <span className="text-sm text-slate-600 hidden md:inline" data-testid="current-user-name">{user?.name}</span>
          </div>
        </div>
      </header>
      <div className="flex max-w-[1600px] mx-auto">
        <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-4rem)] sticky top-16 self-start">
          {sidebar}
        </aside>
        {open && (
          <div className="lg:hidden fixed inset-0 z-30 top-16">
            <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl">{sidebar}</aside>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
